import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, CreditCard } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useSearchParams } from "react-router";
import { daysText, LANGUAGE_TERMS, PLAN_TERMS, type SetupChoicesDto } from "../../../shared/billing";
import { TRANSLATION_LANGS, type TranslationLang } from "../../../shared/constants";
import {
  type NutritionStatusDto,
  nutritionPrice,
  PUBLISH_ACCEPTANCE_TEXT,
} from "../../../shared/nutrition-addon";
import { api } from "../../api/client";
import { Alert, ICON } from "../../components/ui";
import type { ShellContext } from "../AppShell";
import { type Billing, ProfileForm } from "../settings/BillingPage";

// The onboarding wizard after the menu upload (owner 2026-11-02, DEC-107): languages, currency, „Valori
// nutriționale”, company data, activation with the card (0 € today). Every step can be skipped (a very small
// link); nothing here is required to publish the menu.

const LANG_LABEL: Record<TranslationLang, string> = {
  en: "Engleză (English)",
  de: "Germană (Deutsch)",
  fr: "Franceză (Français)",
  it: "Italiană (Italiano)",
  es: "Spaniolă (Español)",
  hu: "Maghiară (Magyar)",
};
const STEPS = ["Meniul", "Limbi", "Moneda", "Valori nutriționale", "Datele firmei", "Activare"] as const;
const euro = (minor: number) => `${(minor / 100).toFixed(2).replace(".", ",").replace(",00", "")} €`;

function Skip({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: "none",
        border: 0,
        padding: 0,
        color: "var(--muted, #6b6b7b)",
        fontSize: 12,
        textDecoration: "underline",
        cursor: "pointer",
        alignSelf: "flex-start",
      }}
    >
      Sari peste
    </button>
  );
}

function Step({ title, sub, children }: { title: string; sub?: ReactNode; children: ReactNode }) {
  return (
    <section className="card" style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 680 }}>
      <div>
        <h2 className="h2" style={{ margin: 0 }}>
          {title}
        </h2>
        {sub && (
          <p className="muted" style={{ margin: "4px 0 0" }}>
            {sub}
          </p>
        )}
      </div>
      {children}
    </section>
  );
}

export function StartWizard() {
  const { membership } = useOutletContext<ShellContext>();
  const rid = membership.restaurant.id;
  const isOwner = membership.role === "owner";
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const base = `/restaurants/${rid}/billing`;
  const setup = useQuery({
    queryKey: ["setup", rid],
    queryFn: () => api<SetupChoicesDto>("GET", `${base}/setup`),
  });
  const billing = useQuery({ queryKey: ["billing", rid], queryFn: () => api<Billing>("GET", base) });
  const nutrition = useQuery({
    queryKey: ["nutrition", rid, "status"],
    queryFn: () =>
      api<{ status: NutritionStatusDto }>("GET", `/restaurants/${rid}/nutrition/status`).then(
        (r) => r.status,
      ),
  });
  const paid = params.get("checkout") === "success" ? params.get("paid") : null;
  const [step, setStep] = useState(() => {
    if (paid === "subscription") return 6;
    if (paid === "nutrition") return 3.5; // the plan question after the add-on payment
    return Number(params.get("step") ?? 1) || 1;
  });
  const [langs, setLangs] = useState<TranslationLang[] | null>(null);
  const [currency, setCurrency] = useState<"RON" | "EUR" | null>(null);
  const [plan, setPlan] = useState<"standard_month" | "standard_year" | null>(null);
  const [nutritionYes, setNutritionYes] = useState(false);
  // The publishing text is accepted before paying (owner 2026-11-03: estimates go on the menu at once).
  const [nutritionAgreed, setNutritionAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (setup.data) {
      setLangs((l) => l ?? setup.data.translationLangs);
      setCurrency((c) => c ?? setup.data.currency);
      setPlan((p) => p ?? setup.data.planChoice);
    }
  }, [setup.data]);

  // Back from Stripe after the add-on: the payment is confirmed by Stripe's webhook a moment later.
  useEffect(() => {
    if (paid) void qc.invalidateQueries({ queryKey: ["nutrition", rid] });
  }, [paid, qc, rid]);

  const go = (n: number) => {
    setError(null);
    setStep(n);
    setParams({ step: String(Math.ceil(n)) }, { replace: true });
  };
  async function save(input: Partial<SetupChoicesDto>, next: number) {
    setBusy(true);
    setError(null);
    try {
      await api("PUT", `${base}/setup`, input);
      await qc.invalidateQueries({ queryKey: ["setup", rid] });
      go(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  async function redirect(path: string, body: unknown) {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ url: string }>("POST", path, body);
      window.location.assign(r.url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  const b = billing.data;
  const extra = Math.max(0, (langs?.length ?? 0) - LANGUAGE_TERMS.freeTranslations);
  const shown = Math.min(6, Math.max(1, Math.ceil(step)));
  const month = b?.plans.find((p) => p.id === "standard_month");
  const year = b?.plans.find((p) => p.id === "standard_year");

  return (
    <>
      <div className="stack" style={{ gap: 6 }}>
        <h1 className="h1">Configurează-ți meniul</h1>
        <p className="page-sub" style={{ margin: 0 }}>
          Pasul {shown} din 6 · {STEPS[shown - 1]}
        </p>
        <ol
          aria-label="Pașii configurării"
          style={{ listStyle: "none", display: "flex", gap: 4, padding: 0, margin: "4px 0 0", maxWidth: 680 }}
        >
          {STEPS.map((s, i) => (
            <li
              key={s}
              aria-current={i + 1 === shown ? "step" : undefined}
              title={s}
              style={{
                flex: 1,
                height: 6,
                borderRadius: 3,
                background: i + 1 <= shown ? "var(--primary, #2a1b4d)" : "var(--line, #e5e5e5)",
              }}
            />
          ))}
        </ol>
      </div>
      {!isOwner && (
        <Alert tone="info">
          Configurarea o face proprietarul contului. Tu poți lucra la meniu între timp.
        </Alert>
      )}
      {error && <Alert tone="error">{error}</Alert>}

      {step === 1 && (
        <Step title="Meniul tău se încarcă" sub="Îl citim în fundal. Între timp, câteva întrebări scurte.">
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <button type="button" className="btn btn-primary" onClick={() => go(2)}>
              Continuă
            </button>
            <Link to="/app/imports" className="btn btn-ghost">
              Vezi importul
            </Link>
          </div>
        </Step>
      )}

      {step === 2 && langs && (
        <Step
          title="În ce limbi vrei meniul?"
          sub={`Româna e limba meniului. O traducere e inclusă; fiecare limbă în plus costă ${euro(LANGUAGE_TERMS.eur.monthMinor)}/lună la planul lunar sau ${euro(LANGUAGE_TERMS.eur.yearMinor)}/an la cel anual, + TVA, abia după cele ${daysText(PLAN_TERMS.trialDays)} gratuite.`}
        >
          <fieldset style={{ border: 0, margin: 0, padding: 0, display: "grid", gap: 8 }}>
            <legend className="sr-only">Limbile traducerii</legend>
            {TRANSLATION_LANGS.map((l) => (
              <label key={l} className="row" style={{ gap: 10, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={langs.includes(l)}
                  onChange={(e) =>
                    setLangs((x) => (e.target.checked ? [...(x ?? []), l] : (x ?? []).filter((y) => y !== l)))
                  }
                />
                <span>{LANG_LABEL[l]}</span>
              </label>
            ))}
          </fieldset>
          <p className="caption muted" style={{ margin: 0 }} aria-live="polite">
            {langs.length === 0
              ? "Fără traducere: meniul doar în română."
              : extra === 0
                ? "Inclus în abonament."
                : `${extra === 1 ? "O limbă" : `${extra} limbi`} în plus: ${extra} × ${euro(LANGUAGE_TERMS.eur.monthMinor)}/lună sau ${extra} × ${euro(LANGUAGE_TERMS.eur.yearMinor)}/an + TVA, după perioada gratuită.`}
          </p>
          <div className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || !isOwner}
              onClick={() => save({ translationLangs: langs }, 3)}
            >
              Continuă
            </button>
          </div>
          <Skip onClick={() => go(3)} />
        </Step>
      )}

      {step === 3 && currency && (
        <Step
          title="În ce monedă sunt prețurile meniului?"
          sub="Prețurile apar exact cum le scrii; nu convertim nimic."
        >
          <fieldset style={{ border: 0, margin: 0, padding: 0, display: "grid", gap: 8 }}>
            <legend className="sr-only">Moneda</legend>
            {(
              [
                ["RON", "Lei (recomandat)", "Pentru restaurantele din România."],
                ["EUR", "Euro", "Dacă meniul tău e deja în euro."],
              ] as const
            ).map(([c, label, hint]) => (
              <label key={c} className="row" style={{ gap: 10, alignItems: "flex-start" }}>
                <input type="radio" name="cur" checked={currency === c} onChange={() => setCurrency(c)} />
                <span>
                  <strong style={{ fontWeight: 600 }}>{label}</strong>
                  <span className="muted caption" style={{ display: "block" }}>
                    {hint}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>
          <div className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || !isOwner}
              onClick={() => save({ currency }, 4)}
            >
              Continuă
            </button>
          </div>
          <Skip onClick={() => go(4)} />
        </Step>
      )}

      {step === 3.5 && (
        <Step
          title={nutrition.data?.active ? "Plata a reușit. Ce plan te interesează?" : "Confirmăm plata…"}
          sub={`Cardul e salvat. Planul pornește după cele ${daysText(PLAN_TERMS.trialDays)} gratuite; îl poți schimba până atunci.`}
        >
          <PlanChoice value={plan} onChange={setPlan} month={month} year={year} />
          <div className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || !isOwner}
              onClick={() => save({ planChoice: plan ?? "standard_year" }, b?.profile ? 6 : 5)}
            >
              Continuă
            </button>
          </div>
          <Skip onClick={() => go(b?.profile ? 6 : 5)} />
        </Step>
      )}

      {step === 4 && (
        <Step
          title="Vrei valorile nutriționale pe meniu?"
          sub="Ingrediente cu gramaje, alergeni, aditivi și valori la 100 g, cerute de Ordinul ANPC 201/2022. AI-ul propune rețetele, tu confirmi."
        >
          {nutrition.data?.active ? (
            <Alert tone="success">Valorile nutriționale sunt deja active.</Alert>
          ) : nutritionYes ? (
            b?.profile ? (
              <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy || !isOwner}
                  onClick={() =>
                    redirect(`${base}/nutrition-checkout`, { kind: "addon", back: "start", accept: true })
                  }
                >
                  <CreditCard {...ICON} aria-hidden="true" /> Plătește{" "}
                  {nutritionPrice("addon", b.payCurrency)}
                </button>
              </div>
            ) : (
              <>
                <p className="caption" style={{ margin: 0 }}>
                  Pentru factură avem nevoie întâi de datele firmei.
                </p>
                <ProfileForm
                  rid={rid}
                  profile={null}
                  canEdit={isOwner}
                  onSaved={() => void qc.invalidateQueries({ queryKey: ["billing", rid] })}
                />
              </>
            )
          ) : (
            <>
              <p style={{ margin: 0 }}>
                <strong style={{ fontWeight: 600 }}>{nutritionPrice("addon", b?.payCurrency)}</strong>
                <span className="muted"> pentru un an, cu 10 analize AI pe lună incluse.</span>
              </p>
              <label className="row" style={{ gap: 8, alignItems: "flex-start" }}>
                <input
                  type="checkbox"
                  checked={nutritionAgreed}
                  onChange={(e) => setNutritionAgreed(e.target.checked)}
                />
                <span className="caption">{PUBLISH_ACCEPTANCE_TEXT}</span>
              </label>
              <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!isOwner || !nutritionAgreed}
                  onClick={async () => {
                    // A marked test account: activated at once, no payment (migration 0044).
                    if (nutrition.data?.complimentary) {
                      setBusy(true);
                      try {
                        await api("POST", `/restaurants/${rid}/nutrition/complimentary`, { accept: true });
                        await qc.invalidateQueries({ queryKey: ["nutrition", rid] });
                        go(5);
                      } catch (e) {
                        setError(e instanceof Error ? e.message : String(e));
                      } finally {
                        setBusy(false);
                      }
                    } else setNutritionYes(true);
                  }}
                >
                  {nutrition.data?.complimentary ? "Da, activează (cont de test, gratuit)" : "Da, vreau"}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => go(5)}>
                  Nu acum
                </button>
              </div>
            </>
          )}
          {nutrition.data?.active && (
            <div className="row" style={{ gap: 8 }}>
              <button type="button" className="btn btn-primary" onClick={() => go(5)}>
                Continuă
              </button>
            </div>
          )}
          <Skip onClick={() => go(5)} />
        </Step>
      )}

      {step === 5 && b && (
        <Step title="Datele firmei, pentru facturi" sub="Le folosim doar pe facturile Meniova.">
          {b.profile && (
            <Alert tone="success">
              <Check {...ICON} aria-hidden="true" /> Date salvate. Le poți corecta mai jos.
            </Alert>
          )}
          <ProfileForm
            key={b.profile ? JSON.stringify(b.profile) : "empty"}
            rid={rid}
            profile={b.profile}
            canEdit={isOwner}
            onSaved={async () => {
              await qc.invalidateQueries({ queryKey: ["billing", rid] });
              go(6);
            }}
          />
          <Skip onClick={() => go(6)} />
        </Step>
      )}

      {step === 6 && b && (
        <Step
          title={paid === "subscription" ? "Gata! Abonamentul e pregătit." : "Activează abonamentul"}
          sub={
            paid === "subscription"
              ? `Cardul e salvat. Azi nu s-a încasat nimic; planul pornește la finalul celor ${daysText(PLAN_TERMS.trialDays)} gratuite.`
              : `Azi plătești 0 €. Salvăm cardul; la finalul celor ${daysText(PLAN_TERMS.trialDays)} gratuite se încasează planul ales. Dacă nu alegi, activăm planul anual. Poți anula oricând înainte.`
          }
        >
          {paid !== "subscription" && (
            <>
              <PlanChoice value={plan} onChange={setPlan} month={month} year={year} />
              {extra > 0 && (
                <p className="caption muted" style={{ margin: 0 }}>
                  Plus {extra === 1 ? "o limbă" : `${extra} limbi`} în plus:{" "}
                  {plan === "standard_month"
                    ? `${extra} × ${euro(LANGUAGE_TERMS.eur.monthMinor)}/lună`
                    : `${extra} × ${euro(LANGUAGE_TERMS.eur.yearMinor)}/an`}{" "}
                  + TVA.
                </p>
              )}
              {!b.paymentsEnabled ? (
                <Alert tone="info">
                  Plățile online nu sunt disponibile momentan. Poți alege planul mai târziu, din Abonament și
                  facturare.
                </Alert>
              ) : !b.profile ? (
                <Alert tone="warning">
                  Completează întâi datele firmei.{" "}
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => go(5)}>
                    Înapoi la datele firmei
                  </button>
                </Alert>
              ) : (
                <div className="row" style={{ gap: 8 }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={busy || !isOwner}
                    onClick={async () => {
                      if (plan) await api("PUT", `${base}/setup`, { planChoice: plan });
                      await redirect(`${base}/checkout`, { planId: plan, back: "start" });
                    }}
                  >
                    <CreditCard {...ICON} aria-hidden="true" /> Salvează cardul (0 € azi)
                  </button>
                </div>
              )}
              <Skip onClick={() => navigate("/app")} />
            </>
          )}
          {paid === "subscription" && (
            <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
              <Link to="/app/imports" className="btn btn-primary">
                Verifică meniul
              </Link>
              {nutrition.data?.active && (
                <Link to="/app/nutrition" className="btn btn-secondary">
                  Valori nutriționale
                </Link>
              )}
              <Link to="/app" className="btn btn-ghost">
                Prezentare generală
              </Link>
            </div>
          )}
        </Step>
      )}
    </>
  );
}

function PlanChoice({
  value,
  onChange,
  month,
  year,
}: {
  value: "standard_month" | "standard_year" | null;
  onChange: (v: "standard_month" | "standard_year") => void;
  month: Billing["plans"][number] | undefined;
  year: Billing["plans"][number] | undefined;
}) {
  const options = [
    [
      "standard_year",
      "Anual (recomandat)",
      year
        ? `${euro(year.introPriceMinor ?? year.priceMinor ?? 0)} primul an, apoi ${euro(year.priceMinor ?? 0)}/an + TVA`
        : "",
    ],
    ["standard_month", "Lunar", month ? `${euro(month.priceMinor ?? 0)}/lună + TVA` : ""],
  ] as const;
  return (
    <fieldset style={{ border: 0, margin: 0, padding: 0, display: "grid", gap: 8 }}>
      <legend className="sr-only">Planul</legend>
      {options.map(([id, label, price]) => (
        <label key={id} className="row" style={{ gap: 10, alignItems: "flex-start" }}>
          <input
            type="radio"
            name="plan"
            checked={(value ?? "standard_year") === id}
            onChange={() => onChange(id)}
          />
          <span>
            <strong style={{ fontWeight: 600 }}>{label}</strong>
            {price && (
              <span className="muted caption" style={{ display: "block" }}>
                {price} · în lei la plată, pentru firmele din România
              </span>
            )}
          </span>
        </label>
      ))}
    </fieldset>
  );
}
